import { FlatList, StyleSheet } from 'react-native';
import { IconButton, ProgressBar as ProgressBarBase } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import Color from 'color';
import styled from 'styled-components/native';

import Button from '@src/components/Button';
import DropDown from '@src/components/DropDown';
import FontIcon from '@src/components/FontIcon';
import Image from '@src/components/Image';
import ImageBackground from '@src/components/ImageBackground';
import ImageIcon from '@src/components/ImageIcon';
import TextInput from '@src/components/TextInput';
import { H1, H2 } from '@src/components/Typography';
import VideoBase from '@src/components/Video';
import { idKeyExtractor, padding } from '@src/configuration/constants';
import { Journey as JourneyType } from '@src/types';

import { MediaPreview } from './types';

type Size = {
  width: number;
  height: number;
};

export const Container = styled(SafeAreaView)`
  background-color: ${({ theme }) => theme.colors.background};
  flex: 1;
`;

export const Title = styled(H1)`
  margin: 20px;
`;

export const SubmitPostButton = styled(Button).attrs(() => ({
  mode: 'contained',
  preset: 'secondary',
  uppercase: false,
}))`
  margin: ${padding}px;
`;

export const Header = styled.View`
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin: 20px ${padding}px 5px ${padding}px;
  height: 120px;
  z-index: -1;
  border-bottom-width: ${StyleSheet.hairlineWidth}px;
`;

export const User = styled.View`
  flex-grow: 1;
  height: 50px;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  margin: 0 ${padding}px;
  margin-bottom: 36px;
  z-index: -1;
`;

export const Username = styled(H1)``;

export const PrivacyPicker = styled(DropDown).attrs(() => ({
  multiSelect: false,
  mode: 'outlined',
  inputProps: {
    dense: true,
    style: {
      fontSize: 12,
      minWidth: 120,
    },
  },
  iconSize: 12,
}))``;

export const Location = styled.View`
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin: 0 ${padding}px;
  height: 75px;
  z-index: -1;
  border-bottom-width: ${StyleSheet.hairlineWidth}px;
`;

export const LocationPicker = styled.TouchableOpacity`
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  height: 50px;
`;

export const LocationPickerLabel = styled(H2)`
  margin: 0 5px;
`;

export const LocationPickerPlaceholder = styled(H2)`
  margin: 0 5px;
  color: ${({ theme }) => theme.colors.disabled};
`;

export const LocationIcon = styled(FontIcon).attrs(() => ({
  name: 'map-marker-alt',
  size: 26,
}))``;

export const PostCaption = styled.ScrollView.attrs(() => ({
  fadingEdgeLength: 20,
  contentContainerStyle: {
    justifyContent: 'center',
    alignItems: 'stretch',
  },
}))`
  margin: 0 ${padding}px;
  margin-top: 5px;
  z-index: -1;
  height: 225px;
`;

export const PostCaptionInput = styled(TextInput)`
  z-index: -1;
`;

export const Media = styled.View`
  justify-content: center;
  align-items: flex-start;
  margin: 0 ${padding}px;
  min-height: 75px;
  z-index: -1;
  border-top-width: ${StyleSheet.hairlineWidth}px;
  border-bottom-width: ${StyleSheet.hairlineWidth}px;
`;

export const MediaPicker = styled.TouchableOpacity`
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
`;

export const MediaPickerPlaceholder = styled(H2)`
  margin: 0 15px;
  color: ${({ theme }) => theme.colors.disabled};
`;

export const MediaTypeContainer = styled.View`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.roundness}px;
  min-height: 30%;
  justify-content: space-around;
  align-items: center;
  margin: 0 ${padding}px;
  z-index: 1001;
`;

export const MediaPreviewWrapper = styled.View`
  position: relative;
`;

export const MediaPreviewList = styled(
  FlatList as new () => FlatList<MediaPreview>
).attrs(() => ({
  removeClippedSubviews: true,
  initialNumToRender: 5,
  maxToRenderPerBatch: 10,
  scrollEventThrottle: 16,
  keyExtractor: (item) => item.name,
  horizontal: true,
  showsHorizontalScrollIndicator: false,
  contentContainerStyle: {
    paddingVertical: padding / 2,
    alignItems: 'center',
  },
}))``;

export const AddMedia = styled.TouchableOpacity`
  width: 50px;
  height: 50px;
  justify-content: center;
  align-items: center;
  align-self: center;
  border-radius: 25px;
  border-width: 1px;
  margin: 25px;
  border-color: ${({ theme }) => theme.colors.primary};
`;

export const AddMediaIcon = styled(FontIcon).attrs(({ theme }) => ({
  name: 'plus',
  size: 25,
  color: theme.colors.primary,
}))``;

export const RemoveButton = styled(IconButton).attrs(({ theme }) => ({
  icon: 'times',
  size: 24,
  color: theme.colors.error,
}))`
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 1;
  background-color: ${({ theme }) =>
    Color(theme.colors.error).alpha(0.2).rgb().string()};
`;

export const Photo = styled(Image).attrs({
  resizeMode: 'cover',
})<Size>`
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  margin: 20px;
`;

export const Thumbnail = styled(ImageBackground).attrs({
  resizeMode: 'cover',
})<Size>`
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
`;

export const Video = styled(VideoBase)<Size>`
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  align-items: stretch;
  margin: 20px;
`;

export const ButtonsWrapper = styled.View`
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
`;

export const MediaTypeButton = styled(Button).attrs(() => ({
  mode: 'text',
  uppercase: false,
}))``;

export const MediaTypeTitle = styled(H2)``;

export const ProgressBar = styled(ProgressBarBase)`
  margin: 5px 0;
`;

export const MediaIcon = styled(FontIcon).attrs(() => ({
  name: 'camera',
  size: 22,
}))``;

export const Journey = styled.View`
  justify-content: center;
  align-items: flex-start;
  margin: 0 ${padding}px;
  z-index: -1;
  height: 75px;
  border-bottom-width: ${StyleSheet.hairlineWidth}px;
`;

export const JourneyPicker = styled.TouchableOpacity`
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  height: 50px;
`;

export const JourneyPickerLabel = styled(H2)`
  margin: 0 15px;
`;

export const JourneyPickerPlaceholder = styled(H2)`
  margin: 0 15px;
  color: ${({ theme }) => theme.colors.disabled};
`;

export const JourneyPickerTitle = styled(H1)`
  margin: 0 15px 10px 15px;
`;

export const JourneyIcon = styled(ImageIcon).attrs(() => ({
  source: require('@src/assets/images/journey.png'),
  size: 22,
}))``;

export const JourneyPickerContainer = styled.View`
  width: 70%;
  height: 70%;
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.roundness}px;
  justify-content: center;
  align-items: center;
  align-self: center;
  padding: 20px 5px;
  z-index: 1000;
`;

export const JourneyList = styled(
  FlatList as new () => FlatList<JourneyType>
).attrs(() => ({
  removeClippedSubviews: true,
  initialNumToRender: 15,
  maxToRenderPerBatch: 10,
  scrollEventThrottle: 16,
  keyExtractor: idKeyExtractor,
  contentContainerStyle: {
    paddingVertical: padding / 2,
  },
}))``;

export const JourneyItem = styled.TouchableOpacity<{ active: boolean }>`
  width: 100%;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  background-color: ${({ active, theme }) =>
    active
      ? Color(theme.colors.primary).alpha(0.1).rgb().string()
      : theme.colors.background};
  border-radius: ${({ theme }) => theme.roundness}px;
  padding: 20px 10px;
  max-height: 40px;
`;

export const JourneyLabel = styled(H2)<{ active: boolean }>`
  padding: 25px 0px;
  color: ${({ active, theme }) =>
    active ? theme.colors.primary : theme.colors.text};
`;
